'use client';

import { useMemo, useState } from 'react';

import type { Color } from '@avodah-inc/types';
import { cn, formatMsTime } from '@avodah-inc/ui/utils';

import { BucketTimer } from '../../atoms/bucket-timer';
import { Card } from '../card';

type TimeBucket = {
  start: number;
  end: number;
  code: string;
};
type TimeComplexityCategory = {
  title: string;
  color: Color;
  buckets?: TimeBucket[];
};
export type TimeComplexityProps = {
  title: string;
  currentTime: number;
  categories: TimeComplexityCategory[];
  externalLinkText?: string;
  externalLinkTextClassName?: string;
  externalLinkOnClick?: () => void;
  children?: React.ReactNode;
  isEmbedded?: boolean;
};

export function TimeComplexity(props: TimeComplexityProps) {
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [oldIndex, setOldIndex] = useState(0);
  const selectedCategory = useMemo(
    () =>
      props.categories[
        selectedCategoryIndex
      ] as unknown as TimeComplexityCategory,
    [selectedCategoryIndex, props.categories],
  );

  const currentTimeInSeconds = Math.round(props.currentTime / 1000);
  const currentTimeInMinutes = Math.floor(currentTimeInSeconds / 60);

  return (
    <Card>
      <div className="p-4" data-dd-privacy="allow">
        <div className="flex justify-between">
          <div className="flex flex-col gap-2">
            <h3 className="font-semibold">{props.title}</h3>
            <div className="flex flex-col gap-3 text-sm">
              {props.categories.map((cat, ind) => (
                <button
                  key={ind}
                  type="button"
                  className={cn(
                    'flex cursor-pointer items-center justify-between gap-x-2',
                    {
                      'text-onLight-medium': ind !== selectedCategoryIndex,
                      'text-onLight-high': ind === selectedCategoryIndex,
                    },
                  )}
                  onMouseOver={() => {
                    if (selectedCategoryIndex !== ind) {
                      setOldIndex(selectedCategoryIndex);
                      setSelectedCategoryIndex(ind);
                    }
                  }}
                  onMouseOut={() => {
                    if (selectedCategoryIndex !== oldIndex) {
                      setSelectedCategoryIndex(oldIndex);
                    }
                  }}
                  onClick={() => {
                    setSelectedCategoryIndex(ind);
                    setOldIndex(ind);
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`h-2 w-2 rounded-full bg-additional-${cat.color}`}
                    />
                    <span>{cat.title}</span>
                  </div>
                  <span>
                    {
                      (
                        cat.buckets
                          ?.filter((o) => o.start <= currentTimeInMinutes)
                          .slice(-1)[0] ?? { code: '-' }
                      ).code
                    }
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <BucketTimer
              isEmbedded={props.isEmbedded}
              time={formatMsTime(props.currentTime)}
              color={selectedCategory.color}
              section={selectedCategory.buckets?.length || 1}
              min={`${
                selectedCategory.buckets && selectedCategory.buckets.length > 0
                  ? (selectedCategory.buckets[0]?.start ?? 0).toFixed(0)
                  : 0
              }min`}
              max={`${(selectedCategory.buckets &&
              selectedCategory.buckets.length > 0
                ? (selectedCategory.buckets[selectedCategory.buckets.length - 1]
                    ?.end ?? 0)
                : 0
              ).toFixed(0)}min`}
              completed={
                selectedCategory.buckets
                  ? selectedCategory.buckets.filter(
                      (o) => o.start <= currentTimeInMinutes,
                    ).length
                  : 0
              }
            />
          </div>
        </div>
        {props.externalLinkText && !props.children && (
          <button
            type="button"
            className={cn('text-primary', props.externalLinkTextClassName)}
            onClick={props.externalLinkOnClick}
          >
            {props.externalLinkText}
          </button>
        )}
        {props.children && <div className="mb-3 mt-4 h-[1px] bg-gray-300" />}
        {props.children}
      </div>
    </Card>
  );
}
